import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { capitalize, difference, intersection, isEqual, parseInt, pick, uniq } from 'lodash';
import cx from 'classnames';
import FlexBox from 'flexbox-react';
import { ThemeProvider } from 'react-css-themr';
import { Input, CardActions, Switch, Tabs, Tab, Checkbox } from 'react-toolbox';
import { can } from '../../../../../services';
import { setEmployeeFilters } from '../../../../../shared/actionCreators/employees';
import TaggedInput from 'components/TaggedInput';
import DatePicker from 'components/DatePicker';
import ReactSelect from 'components/StyledSelect/react_select';
import Card from 'components/Card';
import BreakLine from 'components/BreakLine';
import MessageBox from 'components/MessageBox';
import PhoneInput from 'components/PhoneType/PhoneInput';
import CurrencyInput from 'components/CurrencyInput';
import StyledSelect from 'components/StyledSelect';
import ColoredButton from 'components/ColoredButton';
import PhoneType from 'components/PhoneType';
import ownStyles from './styles.scss';
import styles from 'styles/form';
import RTCard from 'styles/card';
import RTInput from 'styles/input';
import RTSwitch from 'styles/switch';
import RTDropdown from 'styles/dropdown';
import RTTabs from 'styles/tabs';
import RTCheckbox from 'styles/checkbox.scss';
import { error, generateSource } from '../../../../../utils';
import { handleDateSave } from '../../../../../utils/dateUtils.js';
import { STATES, TIMEZONES, EMAIL_VALIDATION_REGEX, IC_ISM_POSITIONS } from 'config/constants';
import { getEmployeeList, getEmployeeLoading } from '../../../../../reducers';
import reactSelectStyles from 'components/ReactSelect/styles';

const MAX_APPT_OPTIONS = [
  { label: '0', value: 0 },
  { label: '1', value: 1 },
  { label: '2', value: 2 },
  { label: '3', value: 3 },
  { label: '4', value: 4 },
  { label: '5', value: 5 },
  { label: '6', value: 6 },
];
const DAYS_OF_THE_WEEK = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const PHONE_TYPES = ['mobile', 'desk', 'fax'];
const STATES_SOURCE = STATES.map(state => ({ value: state, label: state }));
const TIMEZONE_SOURCE = TIMEZONES.map(tz => ({ value: tz, label: tz }));
const theme = { RTInput, RTDropdown, RTCard, RTSwitch, RTTabs };
const largeInput = cx(styles.inputs, styles.largeInput);
const mediumInput = cx(styles.inputs, styles.mediumInput);
const currencyInput = cx(mediumInput, ownStyles.currencyInput);
const smallInput = cx(styles.inputs, styles.smallInput);
const cancelClass = cx([styles.cancelButton, styles.actionButton]);

class EstimatorForm extends Component {
  static tabs = ['Basic Info', 'Pay Rate'];

  constructor(props) {
    super(props);
    this.handleSave = this.handleSave.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleActive = this.handleChange.bind(this, 'active');
    this.handleSelfSchedulable = this.handleChange.bind(this, 'is_self_schedulable');
    this.handlePrimaryType = this.handleChange.bind(this, 'primary_phone_type');
    this.handleSecondaryType = this.handleChange.bind(this, 'secondary_phone_type');
    this.handleState = this.handleChange.bind(this, 'state');
    this.handleMorningMax = this.handleChange.bind(this, 'morning_max_appts');
    this.handleAfternoonMax = this.handleChange.bind(this, 'afternoon_max_appts');
    this.handleEveningMax = this.handleChange.bind(this, 'evening_max_appts');
    this.handleName = this.handleChange.bind(this, 'name');
    this.handleAddress = this.handleChange.bind(this, 'address');
    this.handleCity = this.handleChange.bind(this, 'city');
    this.handleZipCode = this.handleChange.bind(this, 'zip_code');
    this.handleEmail = this.handleChange.bind(this, 'email');
    this.handlePrimaryPhone = this.handleChange.bind(this, 'primary_phone');
    this.handleSecondaryPhone = this.handleChange.bind(this, 'secondary_phone');
    this.handleBadgeChange = this.handleBadgeChange.bind(this);
    this.handleWorkersExp = this.handleChange.bind(this, 'workers_comp_expiration');
    this.handleLiability = this.handleChange.bind(this, 'liability_insurance_expiration');
    this.handleLrrpTraining = this.handleChange.bind(this, 'lrrp_training_expiration');
    this.handleOffReviewDate = this.handleChange.bind(this, 'off_review_date');
    this.handleFloorWizard = this.handleChange.bind(this, 'floor_wizard_ident');
    this.handleTrustedChange = this.handleChange.bind(this, 'trusted');
    this.handleSpigEmployeeChange = this.handleChange.bind(this, 'spig_employee');
    this.handleTraineeRate = this.handleChange.bind(this, 'trainee_rate');
    this.handleTrainingBonus = this.handleChange.bind(this, 'training_bonus');
    this.handlePerAssessmentRate = this.handleChange.bind(this, 'per_assessment_rate');
    this.handleMileageRate = this.handleChange.bind(this, 'mileage_rate');
    this.handleHistory = type => (this.props.estimator && this.props.estimator.id ?
      this.props.handleRateHistory(type) : null);
    this.handleTraineeRateHistory = () => this.handleHistory('trainee_rate');
    this.handleTrainingBonusHistory = () => this.handleHistory('training_bonus');
    this.handlePerAssessmentRateHistory = () => this.handleHistory('per_assessment_rate');
    this.handleMileageRateHistory = () => this.handleHistory('mileage_rate');
    this.handleSelectChange = (key, value) => this.handleChange(key, value.map(v => v.value));
    this.handleWorkrooms = this.handleSelectChange.bind(this, 'workrooms');
    this.handleCoveredZipCodes = this.handleChange.bind(this, 'covered_zip_codes');
    this.handleStartDate = this.handleChange.bind(this, 'estimator_start_date');
    this.handleEndDate = this.handleChange.bind(this, 'estimator_end_date');
    this.handleComments = this.handleChange.bind(this, 'comments');
    const { estimator } = this.props;
    const stateEstimator = (estimator && { ...estimator }) || {
      active: true, primary_phone_type: 'mobile', secondary_phone_type: 'mobile', workrooms: [], assignee_availabilities: [],
    };
    this.state = {
      invalidEmail: false,
      estimator: stateEstimator,
      assigneeAvailabilities: stateEstimator.assignee_availabilities &&
                              stateEstimator.assignee_availabilities.length ?
        this.getAvailabilitiesFromExisting(stateEstimator.assignee_availabilities) :
        this.getAvailabilities(stateEstimator),
    };
  }

  componentDidMount = () => {
    this.fetchEmployees();
  }

  componentWillReceiveProps = nextProps => (nextProps.errors &&
                                            Object.keys(nextProps.errors) &&
                                            !isEqual(this.props.errors, nextProps.errors) ?
    this.goToFirstErroredTab(nextProps) : null);

  componentDidUpdate(prevProps, prevState) {
    const prevIfiWorkroomIds = this.getIfiWorkroomIds(prevState.estimator.workrooms);
    const currentIfiWorkroomIds = this.getIfiWorkroomIds(this.state.estimator.workrooms);

    if (prevIfiWorkroomIds.length !== currentIfiWorkroomIds.length ||
      !prevIfiWorkroomIds.every((id, idx) => id === currentIfiWorkroomIds[idx])) {
      this.fetchEmployees();
    }
  }

  getIfiWorkroomIds = (selectedWorkroomIds = this.state.estimator.workrooms) => {
    const { allWorkrooms = [] } = this.props;
    return (selectedWorkroomIds || []).filter(id =>
      allWorkrooms.some(w => w.id === id && w.vendor === 'IFI'));
  }

  getSupervisorOptions = () => (this.props.employees || []).map(employee => ({
    value: employee.id,
    label: `${employee.name} - ${employee.position || 'Employee'}`,
  }))

  getSupervisorValue = (options) => {
    const { estimator: { assigned_supervisor, supervisor_id } } = this.state;
    const supervisorId = (assigned_supervisor && assigned_supervisor.id) || supervisor_id;
    return supervisorId ? options.find(opt => opt.value === supervisorId) || null : null;
  }

  getStoresFromWorkroom = workroom => workroom.store_with_zips.map(ws => ws.store);

  getStoreList = (workrooms = this.state.estimator.workrooms) => uniq(this.props.allWorkrooms
    .filter(w => workrooms.includes(w.id)).map(w => this.getStoresFromWorkroom(w)).flat());

  getDayTimeChecked = (day, days) => days.includes(day);

  getAvailabilitiesFromExisting = availabilities =>
    this.handleDupedAvailabilities(availabilities.map(a =>
      ({ ...a.assignee_availability, type: 'existing' })));

  getAvailabilities = estimator => (estimator.workrooms.length ?
    this.getStoreList(estimator.workrooms).map(s =>
      this.getNewAvailability(estimator.id || null, s.id)) : []);

  getNewAvailability = (estimatorId, storeId, type = 'new') => ({
    id: null,
    store_id: storeId,
    assignee_type: 'Estimator',
    assignee_id: estimatorId || -1,
    morning: DAYS_OF_THE_WEEK,
    afternoon: DAYS_OF_THE_WEEK,
    evening: DAYS_OF_THE_WEEK,
    type,
  });

  handleSupervisorChange = (selectedOption) => {
    const supervisorId = selectedOption ? selectedOption.value : null;
    this.handleChange('supervisor_id', supervisorId);
  }

  fetchEmployees = () => {
    const ifiWorkroomIds = this.getIfiWorkroomIds();
    if (!ifiWorkroomIds.length) {
      this.props.setEmployeeFilters({
        is_active: true,
        for_workrooms: [-1],
        for_positions: IC_ISM_POSITIONS,
      });
      return;
    }
    this.props.setEmployeeFilters({
      is_active: true,
      for_workrooms: ifiWorkroomIds,
      for_positions: IC_ISM_POSITIONS,
    });
  }

  handleDupedAvailabilities = availabilities => availabilities.map((a1) => {
    const { id, store_id } = a1;
    const dupedAvailabilities = availabilities.filter(a2 => store_id === a2.store_id);
    return dupedAvailabilities.some(dA => dA.id < id) ? ({ ...a1, type: 'delete' }) : a1;
  });

  finalizeAvailabilities = () => {
    const { estimator: { id = null, assignee_availabilities = [] },
            assigneeAvailabilities = [] } = this.state;
    const flatAvailabilities = assignee_availabilities.map(a => a.assignee_availability);
    let editedAvailabilities = assigneeAvailabilities;
    if (!id || !flatAvailabilities.length) return editedAvailabilities;
    if (editedAvailabilities.length) {
      editedAvailabilities = editedAvailabilities.map(a1 =>
        (a1.type === 'new' && flatAvailabilities.some(a2 =>
          a1.store_id === a2.store_id) ? { ...a1, type: 'edit' } : a1));
    } else return flatAvailabilities.map(a => this.getNewAvailability(id, a.store_id, 'delete'));
    const removedAvailabilities = flatAvailabilities.filter(a1 =>
      editedAvailabilities.every(a2 => a1.store_id !== a2.store_id));
    if (removedAvailabilities.length) {
      removedAvailabilities.forEach(a => editedAvailabilities.push(this.getNewAvailability(id, a.store_id, 'delete')));
    }
    const finalAvailabilities = editedAvailabilities.filter(a => a.type !== 'existing');
    return finalAvailabilities;
  };

  handleChange = (key, value) => {
    const { updateDiff = null } = this.props;
    const stateObj = { estimator: this.state.estimator };
    if (key === 'workrooms') stateObj.assigneeAvailabilities = this.handleAvailabilityWorkroomEdit(value);
    const emailChange = key === 'email';
    stateObj.estimator[key] = emailChange ? value.replaceAll(' ', '') : value;
    stateObj.invalidEmail = emailChange && !EMAIL_VALIDATION_REGEX.test(value);
    if (updateDiff) updateDiff(key, value);
    this.setState(stateObj);
  };

  handleAvailabilityWorkroomEdit = (editedWorkrooms) => {
    const { estimator: { id = null, workrooms }, assigneeAvailabilities } = this.state;
    const oldStores = this.getStoreList(workrooms);
    const newStores = this.getStoreList(editedWorkrooms);
    if (editedWorkrooms.length < workrooms.length) {
      return assigneeAvailabilities.filter(a =>
        !difference(oldStores, newStores).some(s => s.id === a.store_id));
    }
    difference(newStores, oldStores).forEach(s => assigneeAvailabilities
                                    .push(this.getNewAvailability(id, s.id)));
    return assigneeAvailabilities;
  };

  handleBadgeChange = (key, value) => {
    const { updateDiff = null } = this.props;
    const estimator = this.state.estimator;
    estimator.badge = estimator.badge || {};
    estimator.badge[key] = value;
    if (updateDiff) updateDiff(key.includes('badge_link') ? key : `badge_${key}`, value);
    this.setState({ estimator });
  };

  handleTimezone = (option) => {
    const { updateDiff = null } = this.props;
    const estimator = this.state.estimator;
    estimator.timezone = option.value;
    if (updateDiff) updateDiff('timezone', option.value);
    this.setState({ estimator });
  };

  handleAvailabilityChange = (storeId, day, period) => {
    const { assigneeAvailabilities } = this.state;
    const editedAvailabilities = assigneeAvailabilities.map((a) => {
      const returnedAvailability = a;
      if (a.store_id === storeId) {
        returnedAvailability[period] = returnedAvailability[period].includes(day) ?
          returnedAvailability[period].filter(d => d !== day) :
          returnedAvailability[period].concat([day]);
        if (a.type === 'existing') returnedAvailability.type = 'edit';
      }
      return returnedAvailability;
    });
    this.setState({ assigneeAvailabilities: editedAvailabilities });
  };

  handleCancel = () => this.props.onCancel();

  handleSave = () => {
    const { workrooms, user_id, badge, active, name, email, primary_phone, primary_phone_type,
            secondary_phone, secondary_phone_type, timezone, workers_comp_expiration,
            liability_insurance_expiration, lrrp_training_expiration, estimator_start_date,
            estimator_end_date, off_review_date, morning_max_appts, afternoon_max_appts,
            evening_max_appts, comments, ...estimator } = this.state.estimator;
    const finalAvailabilities = this.finalizeAvailabilities();
    this.props.onSave({
      ...estimator,
      comments,
      morning_max_appts: parseInt(morning_max_appts),
      afternoon_max_appts: parseInt(afternoon_max_appts),
      evening_max_appts: parseInt(evening_max_appts),
      workers_comp_expiration: handleDateSave(workers_comp_expiration),
      estimator_start_date: handleDateSave(estimator_start_date),
      estimator_end_date: handleDateSave(estimator_end_date),
      liability_insurance_expiration: handleDateSave(liability_insurance_expiration),
      lrrp_training_expiration: handleDateSave(lrrp_training_expiration),
      off_review_date: handleDateSave(off_review_date),
      availabilities: finalAvailabilities,
      user_attributes: {
        id: user_id,
        active,
        name,
        email,
        primary_phone,
        primary_phone_type,
        secondary_phone,
        secondary_phone_type,
        timezone,
        workroom_ids: workrooms,
        badge_attributes: { ...badge, expiration: handleDateSave((badge || {}).expiration) },
      },
    });
  };

  goToFirstErroredTab = (props = this.props) => {
    const { errors, activeTab } = props;
    if (!Object.keys(errors).length) return;
    const tabErrors = this.tabErrors(props);
    // if there are errors on this tab, do nothing
    if (tabErrors[EstimatorForm.tabs[activeTab]]) return;
    const firstErroredTab = EstimatorForm.tabs.indexOf(Object.keys(tabErrors)
                                              .filter(t => tabErrors[t] > 0)[0]);
    this.props.onTabChange(Math.max(firstErroredTab, 0));
  };

  tabErrors = (props = this.props) => {
    const { errors } = props;
    if (!Object.keys(errors).length) return {};
    const payRateErrors = pick(errors, [
      'per_assessment_rate', 'mileage_rate', 'trainee_rate', 'training_bonus',
    ]);
    // Basic Info errors are all those that are not pay rate errors
    const basicInfoErrors = {};
    Object.keys(errors).forEach((e) => {
      if (!payRateErrors[e]) basicInfoErrors[e] = errors[e];
    });
    return {
      'Basic Info': Object.keys(basicInfoErrors).length,
      'Pay Rate': Object.keys(payRateErrors).length,
    };
  };

  sortEstimatorZips = zips => (zips && zips.length > 0 ? zips.sort((a, b) => a - b) : []);

  filterStoreDisplay = availabilities => availabilities.filter((a) => {
    if (a.type === 'delete') return false;
    const { estimator: { covered_zip_codes = [] } } = this.state;
    const store = this.getStoreList().find(s => s.id === a.store_id);
    const storeZips = store ? store.covered_zip_codes : [];
    return storeZips.length && covered_zip_codes.length &&
      intersection(storeZips, covered_zip_codes).length;
  });

  renderTitle = (estimator, isEdit) => {
    const title = isEdit ? (`${estimator.name} (${estimator.id})` || 'Edit Estimator') : 'Add Estimator';
    const switchClassNames = cx({ [styles.switchTitleAdd]: !isEdit, [styles.switchTitle]: true });
    return (<div>
      <FlexBox justifyContent="space-between" className={styles.divider}>
        <div>
          <h5 className={styles.cardTitle}>{title}</h5>
          <p className={styles.cardSubtitle}>
            Please ensure that all fields contain accurate information
          </p>
        </div>
        <Switch
          name="active"
          label="Account Active"
          onChange={this.handleActive}
          className={switchClassNames}
          checked={estimator.active}
        />
      </FlexBox>
    </div>);
  };

  renderByTimePeriodAndDay = (storeId, period, days) => <Fragment>
    <h5 className={styles.cardSubtitle}>{period.toUpperCase()}</h5>
    <BreakLine />
    {DAYS_OF_THE_WEEK.map(d => (<Checkbox
      key={`${storeId}${period}${d}`}
      checked={this.getDayTimeChecked(d, days)}
      label={capitalize(d)}
      onChange={() => this.handleAvailabilityChange(storeId, d, period)}
      className={styles.checkboxListItem}
      theme={RTCheckbox}
    />))}
    <BreakLine />
  </Fragment>;

  renderAvailabilityDisplayName = (availability) => {
    const store = this.getStoreList().find(s => s.id === availability.store_id);
    return `${store.name} - ${store.number}`;
  };

  renderAvailabilityByStore = availabilities => <Fragment>
    {(availabilities || []).length > 0 && this.filterStoreDisplay(availabilities).map(a => (<FlexBox
      key={a.store_id} flexWrap="wrap" className={styles.divider}
    >
      <h5 className={styles.cardTitle}>{this.renderAvailabilityDisplayName(a)}</h5>
      <BreakLine />
      {this.renderByTimePeriodAndDay(a.store_id, 'morning', a.morning)}
      {this.renderByTimePeriodAndDay(a.store_id, 'afternoon', a.afternoon)}
      {this.renderByTimePeriodAndDay(a.store_id, 'evening', a.evening)}
    </FlexBox>))}
  </Fragment>;

  render = () => {
    const { estimator, assigneeAvailabilities, invalidEmail } = this.state;
    const { role, errors, allWorkrooms, activeTab, onTabChange, position = '', mvw = {} } = this.props;
    const isEdit = !!estimator.id;
    const gError = error('global', errors);
    const workroomOptions = generateSource(allWorkrooms);
    const tabErrors = this.tabErrors();
    const badge = estimator.badge || {};
    const hasIfiWorkrooms = this.getIfiWorkroomIds().length > 0;
    const canViewSupervisor = can(role, 'view', 'SUPERVISOR_INFO', { position, mvw });
    const showSupervisorSelect = canViewSupervisor && hasIfiWorkrooms;
    const supervisorOptions = showSupervisorSelect ? this.getSupervisorOptions() : [];
    const supervisorValue = showSupervisorSelect ?
      this.getSupervisorValue(supervisorOptions) : null;
    return (<ThemeProvider theme={theme}>
      <Card>
        {this.renderTitle(estimator, isEdit)}
        {gError ? <MessageBox error message={gError} /> : null}
        <Tabs index={activeTab} onChange={onTabChange} className={RTTabs.absoluteTabs}>
          <Tab
            label="Basic Info"
            icon={tabErrors['Basic Info'] ? 'error' : ''}
            className={`${tabErrors['Basic Info'] ? RTTabs.inError : ''}`}
          >
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <Input
                label="Name"
                className={largeInput}
                value={estimator.name}
                onChange={this.handleName}
                error={error('user.name', errors)}
                required
              />
              <Input
                label="Street Address"
                className={largeInput}
                value={estimator.address}
                onChange={this.handleAddress}
                error={error('address', errors)}
                required
              />
              <Input
                label="City"
                className={mediumInput}
                value={estimator.city}
                onChange={this.handleCity}
                error={error('city', errors)}
                required
              />
              <StyledSelect
                className={largeInput}
                label="State"
                onChange={this.handleState}
                options={STATES_SOURCE}
                value={estimator.state}
                placeholder="Select State"
                error={error('state', errors)}
                required
              />
              <Input
                label="Zip Code"
                className={smallInput}
                value={estimator.zip_code}
                onChange={this.handleZipCode}
                error={error('zip_code', errors)}
                required
              />
              <BreakLine />
              <Input
                label="Email"
                type="email"
                value={estimator.email}
                className={largeInput}
                onChange={this.handleEmail}
                error={invalidEmail ? 'Invalid email address' : error('user.email', errors)}
                required
              />
              <PhoneInput
                label="Primary Phone"
                type="tel"
                value={estimator.primary_phone}
                className={largeInput}
                onChange={this.handlePrimaryPhone}
                error={error('user.primary_phone', errors)}
                required
              >
                <PhoneType
                  source={PHONE_TYPES}
                  value={estimator.primary_phone_type}
                  onChange={this.handlePrimaryType}
                />
              </PhoneInput>
              <PhoneInput
                label="Secondary Phone"
                type="tel"
                value={estimator.secondary_phone}
                className={largeInput}
                onChange={this.handleSecondaryPhone}
                error={error('user.secondary_phone', errors)}
              >
                <PhoneType
                  source={PHONE_TYPES}
                  value={estimator.secondary_phone_type}
                  onChange={this.handleSecondaryType}
                />
              </PhoneInput>
              <ReactSelect
                className={mediumInput}
                label="Time Zone"
                placeholder={'Select Time Zone'}
                onChange={this.handleTimezone}
                options={TIMEZONE_SOURCE}
                value={estimator.timezone}
                error={error('timezone', errors)}
                required
              />
            </FlexBox>
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <Input
                label="Badge number"
                className={largeInput}
                value={badge.number}
                onChange={v => this.handleBadgeChange('number', v)}
                error={error('user.badge.number', errors)}
                required
              />
              <DatePicker
                label="Badge expiration"
                className={largeInput}
                value={badge.expiration}
                onChange={v => this.handleBadgeChange('expiration', v)}
                error={error('user.badge.expiration', errors)}
              />
              <Input
                label="Badge Link"
                className={largeInput}
                value={badge.badge_link || ''}
                onChange={v => this.handleBadgeChange('badge_link', v)}
                error={error('badge_link', errors)}
              />
              <DatePicker
                label="Worker's comp. expiration"
                className={largeInput}
                value={estimator.workers_comp_expiration}
                onChange={this.handleWorkersExp}
                error={error('workers_comp_expiration', errors)}
              />
              <DatePicker
                label="General liability insurance expiration"
                className={largeInput}
                value={estimator.liability_insurance_expiration}
                onChange={this.handleLiability}
                error={error('liability_insurance_expiration', errors)}
              />
              <DatePicker
                label="LRRP training expiration"
                className={largeInput}
                value={estimator.lrrp_training_expiration}
                onChange={this.handleLrrpTraining}
                error={error('lrrp_training_expiration', errors)}
              />
              <DatePicker
                label="Off Review Date"
                className={largeInput}
                value={estimator.off_review_date}
                onChange={this.handleOffReviewDate}
                error={error('off_review_date', errors)}
              />
              <Input
                label="Floorwizard id"
                className={largeInput}
                value={estimator.floor_wizard_ident}
                onChange={this.handleFloorWizard}
                error={error('floor_wizard_ident', errors)}
                required
              />
              <DatePicker
                label="Start Date"
                className={largeInput}
                value={estimator.estimator_start_date}
                onChange={this.handleStartDate}
                error={error('estimator_start_date', errors)}
              />
              <DatePicker
                label="End Date"
                className={largeInput}
                value={estimator.estimator_end_date}
                onChange={this.handleEndDate}
                error={error('estimator_end_date', errors)}
              />
              <BreakLine />
              <Checkbox
                checked={estimator.trusted}
                label="Bypass Review, Send Assessments Directly to Store"
                onChange={this.handleTrustedChange}
                className={styles.checkboxListItem}
                theme={RTCheckbox}
              />
              <Checkbox
                checked={estimator.is_self_schedulable}
                label="Self Schedulable"
                onChange={this.handleSelfSchedulable}
                className={styles.checkboxListItem}
                theme={RTCheckbox}
              />
              <Checkbox
                checked={estimator.spig_employee}
                label="SPIG Employee"
                onChange={this.handleSpigEmployeeChange}
                className={styles.checkboxListItem}
                theme={RTCheckbox}
              />
            </FlexBox>
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <Input
                multiline
                label="Notes/Comments"
                value={estimator.comments || ''}
                className={styles.textArea}
                error={error('comments', errors)}
                onChange={this.handleComments}
              />
            </FlexBox>
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <StyledSelect
                className={mediumInput}
                label="Morning Max Appts."
                onChange={this.handleMorningMax}
                options={MAX_APPT_OPTIONS}
                value={estimator.morning_max_appts}
                placeholder="Select Max Appts."
                error={error('morning_max_appts', errors)}
                required
              />
              <StyledSelect
                className={mediumInput}
                label="Afternoon Max Appts."
                onChange={this.handleAfternoonMax}
                options={MAX_APPT_OPTIONS}
                value={estimator.afternoon_max_appts}
                placeholder="Select Max Appts."
                error={error('afternoon_max_appts', errors)}
                required
              />
              <StyledSelect
                className={mediumInput}
                label="Evening Max Appts."
                onChange={this.handleEveningMax}
                options={MAX_APPT_OPTIONS}
                value={estimator.evening_max_appts}
                placeholder="Select Max Appts."
                error={error('evening_max_appts', errors)}
                required
              />
            </FlexBox>
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <TaggedInput
                label="Assign Zip Code(s) to Estimator"
                id="covered_zip_codes"
                values={this.sortEstimatorZips(estimator.covered_zip_codes)}
                onChange={this.handleCoveredZipCodes}
                errors={errors.covered_zip_codes || []}
              />
              <ReactSelect
                multi
                resetValue={[]}
                name="estimatorWorkrooms"
                className={reactSelectStyles.selectWrapper}
                options={workroomOptions}
                value={estimator.workrooms}
                onChange={this.handleWorkrooms}
                label="Assign workroom(s) to estimator"
                placeholder="Select workroom(s)"
              />
              {showSupervisorSelect && (
                // "Supervisor" is labeled as "Coordinator" in the UI since
                // that's more familiar to IFI users, but the value is still
                // stored as "supervisor_id" in the backend.
                <ReactSelect
                  className={largeInput}
                  label="Coordinator"
                  placeholder="Select Coordinator"
                  options={supervisorOptions}
                  value={supervisorValue}
                  onChange={this.handleSupervisorChange}
                  clearable
                  isLoading={this.props.employeeLoading}
                />
              )}
            </FlexBox>
            {assigneeAvailabilities.length > 0 &&
              this.renderAvailabilityByStore(assigneeAvailabilities)}
          </Tab>
          {can(role, 'update', 'pay_rate') && <Tab
            label="Pay Rate"
            icon={tabErrors['Pay Rate'] ? 'error' : ''}
            className={`${tabErrors['Pay Rate'] ? RTTabs.inError : ''}`}
          >
            <FlexBox
              flexWrap="wrap"
              className={styles.divider}
              style={{ borderBottomWidth: 0 }}
            >
              <CurrencyInput
                label="Per assessment rate"
                className={currencyInput}
                value={estimator.per_assessment_rate || ''}
                onChange={this.handlePerAssessmentRate}
                error={error('per_assessment_rate', errors)}
                required
              />
              <CurrencyInput
                label="Mileage rate"
                className={currencyInput}
                value={estimator.mileage_rate || ''}
                onChange={this.handleMileageRate}
                error={error('mileage_rate', errors)}
                required
              />
            </FlexBox>
            <FlexBox flexWrap="wrap" className={styles.divider}>
              <CurrencyInput
                label="Trainee rate"
                className={currencyInput}
                value={estimator.trainee_rate || ''}
                onChange={this.handleTraineeRate}
                error={error('trainee_rate', errors)}
                required
              />
              <CurrencyInput
                label="Training bonus"
                className={currencyInput}
                value={estimator.training_bonus || ''}
                onChange={this.handleTrainingBonus}
                error={error('training_bonus', errors)}
                required
              />
            </FlexBox>
          </Tab>}
        </Tabs>
        <FlexBox justifyContent="flex-end" flexWrap="wrap">
          <CardActions className={styles.actionsWrapper}>
            <ColoredButton
              className={cancelClass}
              responsive
              color="gray"
              label="Cancel"
              onClick={this.handleCancel}
            />
            <ColoredButton
              className={styles.actionButton}
              responsive
              color="green"
              label={isEdit ? 'Save Changes' : 'Add estimator'}
              onClick={this.handleSave}
              disabled={invalidEmail}
            />
          </CardActions>
        </FlexBox>
      </Card>
    </ThemeProvider>);
  };
}

EstimatorForm.propTypes = {
  role: PropTypes.string.isRequired,
  estimator: PropTypes.object,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  errors: PropTypes.object,
  allWorkrooms: PropTypes.array,
  employees: PropTypes.array,
  employeeLoading: PropTypes.bool,
  setEmployeeFilters: PropTypes.func.isRequired,
  activeTab: PropTypes.number,
  onTabChange: PropTypes.func,
  handleRateHistory: PropTypes.func,
  updateDiff: PropTypes.func,
  position: PropTypes.string,
  mvw: PropTypes.object,
};

const getEmployeeOptions = createSelector(
  getEmployeeList,
  employees => employees || [],
);

const mapStateToProps = state => ({
  employees: getEmployeeOptions(state),
  employeeLoading: getEmployeeLoading(state),
});

const mapDispatchToProps = dispatch => ({
  setEmployeeFilters: filters => dispatch(setEmployeeFilters(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EstimatorForm);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/estimators/components/estimatorForm.jsx